import React, {type FC, useEffect} from 'react';
import {DRAWER_HEADER_HEIGHT_LG} from "../main/drawer/DrawerConstants.ts";

const OVERFLOW_OFFSET = 100;

// Taken from https://github.com/mui/mui-x/issues/15466#issuecomment-2536296973
// Toolbar is not include in this solution, It seems it is due to toolbar being out of virtual scroll
// This solution doesn't cover initial state of scrollbar, since at time of initialization scrollbar is inside visible
//  viewport. Therefor a scroll event is required to set offset of scrollbarContainer
type Props = {
    children: React.ReactNode
    stickyHeader?: boolean
    stickyScrollbar?: boolean
    topOffset?: number
    topPadding?: number
}
export const WithStickyComponent: FC<Props> = ({
                                                   children,
                                                   stickyHeader,
                                                   stickyScrollbar,
                                                   topOffset = DRAWER_HEADER_HEIGHT_LG,
                                                   topPadding = OVERFLOW_OFFSET
                                               }) => {
    // noinspection TypeScriptValidateTypes
    const containerRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        // Cache DOM elements
        const virtualScroller = container.querySelector('.MuiDataGrid-virtualScroller');
        const topContainer: HTMLElement | null = container.querySelector('.MuiDataGrid-topContainer');
        if (!virtualScroller || !topContainer) return;

        const handleScroll = () => {
            const scrollbarContainer: HTMLElement | null = container.querySelector('.MuiDataGrid-scrollbar');
            // Get the virtual scroller's bounds
            const scrollerRect = virtualScroller.getBoundingClientRect();
            const windowTop = window.scrollY + topOffset;
            const scrollerTop = scrollerRect.top + window.scrollY;
            const scrollerBottom = scrollerTop + scrollerRect.height;
            if (stickyHeader) {
                // Check if window top is within the virtualScroller's height
                if (windowTop >= scrollerTop && windowTop <= scrollerBottom) {
                    const difference = windowTop - scrollerTop;
                    topContainer.style.top = `${difference - topPadding}px`;
                    // Adding a box shadow here does not work with vertical scroll
                    topContainer.classList.add('muix-custom-sticky-container');
                } else {
                    topContainer.style.top = '';
                    topContainer.classList.remove('muix-custom-sticky-container');
                }
            }
            if (stickyScrollbar && scrollbarContainer) {
                // Check if window bottom is within the virtualScroller's height
                const windowBottom = window.scrollY + window.innerHeight;
                if (windowBottom >= scrollerTop && windowBottom <= scrollerBottom) {
                    const difference = scrollerBottom - windowBottom;
                    scrollbarContainer.style.bottom = `${difference}px`;
                    scrollbarContainer.classList.add('muix-custom-sticky-container');
                } else {
                    scrollbarContainer.style.bottom = '';
                    scrollbarContainer.classList.remove('muix-custom-sticky-container');
                }
            }
        };

        // Add scroll event listeners
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleScroll);

        // Initial check
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [topOffset, topPadding, stickyHeader, stickyScrollbar]);

    if (!children) return <></>;

    return <div className="sticky-header-wrapper" ref={containerRef}>{children}</div>;
}
